import React from "react";
import { Check } from "lucide-react";

/**
 * Linear stepper for the 3-stage Yestimate Intake Companion.
 *
 * Mirrors the Call Companion's "Phase X of N" progress strip so the FOH gets
 * a consistent operational rhythm across both modal-style call workflows
 * and the full-page intake flow.
 *
 *   Props:
 *     stages       — [{ id, label, sublabel? }]
 *     currentIndex — int (0-based)
 *     onJumpBack   — (idx) => void   (only allows jumping to completed stages)
 */
export default function IntakeStepper({ stages, currentIndex, onJumpBack }) {
  return (
    <div
      className="rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm"
      data-testid="intake-stepper"
    >
      <ol className="flex items-center gap-2 sm:gap-4">
        {stages.map((stage, idx) => {
          const isComplete = idx < currentIndex;
          const isActive = idx === currentIndex;
          const canJump = isComplete && onJumpBack;

          return (
            <React.Fragment key={stage.id}>
              <li className="flex-1 min-w-0">
                <button
                  type="button"
                  disabled={!canJump}
                  onClick={() => canJump && onJumpBack(idx)}
                  className={`group w-full text-left flex items-center gap-3 ${
                    canJump ? "cursor-pointer" : "cursor-default"
                  }`}
                  data-testid={`stepper-stage-${stage.id}`}
                  data-active={isActive ? "true" : "false"}
                  data-complete={isComplete ? "true" : "false"}
                >
                  <span
                    className={`flex items-center justify-center w-9 h-9 rounded-full text-[14px] font-bold border-2 transition-colors ${
                      isComplete
                        ? "bg-emerald-500 border-emerald-500 text-white"
                        : isActive
                        ? "bg-teal-600 border-teal-600 text-white shadow-md shadow-teal-500/30"
                        : "bg-white border-slate-300 text-slate-400"
                    } ${canJump ? "group-hover:bg-emerald-600 group-hover:border-emerald-600" : ""}`}
                  >
                    {isComplete ? <Check className="w-4 h-4" /> : idx + 1}
                  </span>
                  <span className="min-w-0">
                    <span
                      className={`block text-[11px] uppercase tracking-[0.18em] font-semibold ${
                        isActive
                          ? "text-teal-700"
                          : isComplete
                          ? "text-emerald-700"
                          : "text-slate-400"
                      }`}
                    >
                      Stage {idx + 1}
                    </span>
                    <span
                      className={`block text-[15px] font-semibold truncate ${
                        isActive
                          ? "text-slate-900"
                          : isComplete
                          ? "text-slate-700"
                          : "text-slate-400"
                      }`}
                    >
                      {stage.label}
                    </span>
                    {stage.sublabel && (
                      <span
                        className={`block text-[12px] truncate ${
                          isActive ? "text-slate-500" : "text-slate-400"
                        }`}
                      >
                        {stage.sublabel}
                      </span>
                    )}
                  </span>
                </button>
              </li>
              {idx < stages.length - 1 && (
                <li
                  aria-hidden="true"
                  className={`h-px flex-shrink-0 w-6 sm:w-12 ${
                    idx < currentIndex ? "bg-emerald-400" : "bg-slate-200"
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </ol>
    </div>
  );
}
